import React, {useEffect, useState} from 'react';
import {Card, Col, DatePicker, Pagination, Row, Select, Statistic, Table, Tag, Typography} from 'antd';
import type {ColumnsType} from 'antd/es/table';
import dayjs from 'dayjs';
import {tokenStatsService, TokenStatsSummary, UserTokenStats} from '../../services/tokenStatsService';
import styles from './TokenStatsPage.module.css';

const {Title} = Typography;
const {RangePicker} = DatePicker;

interface TableRow extends UserTokenStats {
    key: number;
}

const TokenStatsPage: React.FC = () => {
    const [summary, setSummary] = useState<TokenStatsSummary | null>(null);
    const [statsData, setStatsData] = useState<TableRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);
    const [selectedModel, setSelectedModel] = useState<string | undefined>(undefined);
    const [page, setPage] = useState(0);
    const [size] = useState(10);
    const [total, setTotal] = useState(0);

    // 获取可用的模型列表
    const modelOptions = summary?.modelBreakdown
        ? Object.keys(summary.modelBreakdown).map(model => ({label: model, value: model}))
        : [];

    useEffect(() => {
        loadSummary();
    }, [dateRange, selectedModel]);

    useEffect(() => {
        loadStats();
    }, [page, dateRange, selectedModel]);

    const loadSummary = async () => {
        try {
            const startDate = dateRange?.[0]?.format('YYYY-MM-DD');
            const endDate = dateRange?.[1]?.format('YYYY-MM-DD');
            const data = await tokenStatsService.getSummary(startDate, endDate, selectedModel);
            setSummary(data);
        } catch (error) {
            console.error('加载统计摘要失败', error);
        }
    };

    const loadStats = async () => {
        setLoading(true);
        try {
            const startDate = dateRange?.[0]?.format('YYYY-MM-DD');
            const endDate = dateRange?.[1]?.format('YYYY-MM-DD');
            const data = await tokenStatsService.getAllUserStats(page, size, startDate, endDate, selectedModel);
            setStatsData(data.list.map((item, index) => ({...item, key: index})));
            setTotal(data.total);
        } catch (error) {
            console.error('加载统计数据失败', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDateChange = (dates: [dayjs.Dayjs, dayjs.Dayjs] | null) => {
        setDateRange(dates);
        setPage(0);
    };

    const handleModelChange = (value: string | undefined) => {
        setSelectedModel(value);
        setPage(0);
    };

    const handlePageChange = (newPage: number) => {
        setPage(newPage - 1);
    };

    const columns: ColumnsType<TableRow> = [
        {
            title: '用户ID',
            dataIndex: 'userId',
            key: 'userId',
            width: 120,
        },
        {
            title: '模型',
            dataIndex: 'modelName',
            key: 'modelName',
            width: 150,
            render: (model: string) => <Tag color="blue">{model}</Tag>,
        },
        {
            title: '日期',
            dataIndex: 'statsDate',
            key: 'statsDate',
            width: 120,
            render: (date: string) => date || '-',
        },
        {
            title: 'Prompt Tokens',
            dataIndex: 'totalPromptTokens',
            key: 'totalPromptTokens',
            width: 140,
            align: 'right',
            render: (value: number) => value?.toLocaleString() || '0',
        },
        {
            title: 'Completion Tokens',
            dataIndex: 'totalCompletionTokens',
            key: 'totalCompletionTokens',
            width: 160,
            align: 'right',
            render: (value: number) => value?.toLocaleString() || '0',
        },
        {
            title: '总Tokens',
            dataIndex: 'totalTokens',
            key: 'totalTokens',
            width: 120,
            align: 'right',
            render: (value: number) => (
                <span style={{color: 'var(--pt-primary)', fontWeight: 600}}>
                    {value?.toLocaleString() || '0'}
                </span>
            ),
        },
        {
            title: '费用(USDT)',
            dataIndex: 'totalCost',
            key: 'totalCost',
            width: 120,
            align: 'right',
            render: (value: number) => value?.toFixed(8) || '0',
        },
        {
            title: '调用次数',
            dataIndex: 'callCount',
            key: 'callCount',
            width: 100,
            align: 'right',
            render: (value: number) => value || 0,
        },
    ];

    return (
        <div className={styles.container}>
            <Title level={3} className={styles.pageTitle}>Token消耗统计</Title>

            {/* 顶部汇总卡片 */}
            <Row gutter={16} className={styles.summaryRow}>
                <Col span={6}>
                    <Card className={styles.statCard}>
                        <Statistic
                            title={<span className={styles.statTitle}>今日消耗Tokens</span>}
                            value={summary?.todayTokens || 0}
                            precision={0}
                            valueStyle={{color: 'var(--pt-bull)', fontSize: '24px'}}
                        />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card className={styles.statCard}>
                        <Statistic
                            title={<span className={styles.statTitle}>今日费用</span>}
                            value={summary?.todayCost || 0}
                            precision={4}
                            valueStyle={{color: 'var(--pt-primary)', fontSize: '24px'}}
                            suffix="USDT"
                        />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card className={styles.statCard}>
                        <Statistic
                            title={<span className={styles.statTitle}>总消耗Tokens</span>}
                            value={summary?.totalTokens || 0}
                            precision={0}
                            valueStyle={{color: 'var(--pt-bull)', fontSize: '24px'}}
                        />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card className={styles.statCard}>
                        <Statistic
                            title={<span className={styles.statTitle}>总费用</span>}
                            value={summary?.totalCost || 0}
                            precision={4}
                            valueStyle={{color: 'var(--pt-primary)', fontSize: '24px'}}
                            suffix="USDT"
                        />
                    </Card>
                </Col>
            </Row>

            {/* 筛选栏 */}
            <Card className={styles.filterCard}>
                <Row gutter={16} align="middle">
                    <Col>
                        <span className={styles.filterLabel}>日期范围：</span>
                        <RangePicker
                            value={dateRange}
                            onChange={handleDateChange}
                            allowClear
                        />
                    </Col>
                    <Col>
                        <span className={styles.filterLabel}>模型筛选：</span>
                        <Select
                            placeholder="全部模型"
                            value={selectedModel}
                            onChange={handleModelChange}
                            allowClear
                            style={{width: 200}}
                            options={modelOptions}
                        />
                    </Col>
                </Row>
            </Card>

            {/* 数据表格 */}
            <Card className={styles.tableCard}>
                <Table
                    columns={columns}
                    dataSource={statsData}
                    loading={loading}
                    pagination={false}
                    scroll={{x: 1000}}
                />
                <div className={styles.pagination}>
                    <Pagination
                        current={page + 1}
                        pageSize={size}
                        total={total}
                        onChange={handlePageChange}
                        showSizeChanger={false}
                        showTotal={(total) => `共 ${total} 条`}
                    />
                </div>
            </Card>
        </div>
    );
};

export default TokenStatsPage;
